import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UpsertThreatTypeModalComponent } from '../shared/modals/upsert-threat-type-modal/upsert-threat-type-modal.component';
import { ConfirmationModalComponent } from '../shared/modals/confirmation-modal/confirmation-modal.component';
import { AgilityRiskService } from '../../providers/agility-risk.service';
import { ThreatType } from '../../models/threat-type';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AgilityRiskMessageService } from '../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-threat-types',
  templateUrl: './threat-types.component.html',
  styleUrls: ['./threat-types.component.scss']
})
export class ThreatTypesComponent implements OnInit, OnDestroy {
  public upsertDynamicDialogRef: DynamicDialogRef;
  public confirmationDynamicDialogRef: DynamicDialogRef;
  public threatTypes: Array<ThreatType> = [];
  public columns: Array<{ header: string }> = [
    {
      header: 'Name'
    }
  ];
  public threatTypesReorderSubject = new Subject<any>();
  public loading: boolean;

  constructor(
    public dialogService: DialogService,
    private agilityRiskService: AgilityRiskService,
    private translateService: TranslateService,
    public messageService: AgilityRiskMessageService) {
    this.threatTypesReorderSubject
      .pipe(debounceTime(1000))
      .subscribe((res: Array<ThreatType>) => {
        this.agilityRiskService.putThreatTypesPosition(res)
          .subscribe(() => {
            this.refreshData();
          });
      });
  }

  ngOnInit(): void {
    this.loading = true;
    this.agilityRiskService.getThreatTypes().subscribe((result: Array<ThreatType>) => {
      this.threatTypes = result;
      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.upsertDynamicDialogRef?.destroy();
    this.confirmationDynamicDialogRef?.destroy();
    this.threatTypesReorderSubject.unsubscribe();
  }

  openUpsertThreatTypeModal(threatType?: any): void {
    this.upsertDynamicDialogRef = this.dialogService.open(UpsertThreatTypeModalComponent, {
      data: {
        threatType: threatType ? Object.assign({}, threatType) : new ThreatType() // clone it. don't send original or else you will updating original via 2-way binding
      },
      header: (threatType ? this.translateService.instant('common.edit') : this.translateService.instant('common.add')) + ' ' + this.translateService.instant('threatTypes.threatType'),
      width: '50%'
    });

    this.upsertDynamicDialogRef.onClose.subscribe((result: ThreatType) => {
      if (result) {
        this.agilityRiskService.saveThreatType(result).subscribe(
          () => {
            this.refreshData();
            this.messageService.success(this.translateService.instant('common.saved'));
          },
          () => {
            this.messageService.error(this.translateService.instant('common.failedToSave'));
          }
        );
      }
    });
  }

  confirmDeleteThreatType(threatType: any): void {
    this.confirmationDynamicDialogRef = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: this.translateService.instant('threatTypes.deleteThreatTypeBody')
      },
      header: this.translateService.instant('threatTypes.deleteThreatType'),
      width: '35%'
    });

    this.confirmationDynamicDialogRef.onClose.subscribe((result: any) => {
      if (result) {
        this.agilityRiskService.deleteThreatType(threatType).subscribe(
          () => {
            this.refreshData();
            this.messageService.success(this.translateService.instant('common.deleted'));
          },
          () => {
            this.messageService.success(this.translateService.instant('common.failedToDeleted'));
          }
        );
      }
    });
  }

  refreshData(): void {
    this.agilityRiskService.getThreatTypes().subscribe((getThreatTypesRes: Array<ThreatType>) => {
      // refresh the table data. primeng/table doesn't refresh data properly unless done this way. revisit later.
      this.threatTypes = [];

      getThreatTypesRes.forEach((at: ThreatType) => {
        this.threatTypes.push(at);
      });
    });
  }

  onThreatTypeReorder(dragIndex: number, dropIndex: number): void {
    const draggedThreatType: ThreatType = this.threatTypes[dragIndex];
    this.threatTypes.splice(dragIndex, 1);
    this.threatTypes.splice(dropIndex, 0, draggedThreatType);
    this.threatTypesReorderSubject.next(this.reorderThreatTypes(this.threatTypes));
  }

  reorderThreatTypes(orderedThreatTypes: Array<ThreatType>): Array<ThreatType> {
    let newSequenceNumber = 0;
    orderedThreatTypes.forEach((threatType: ThreatType) => {
      const tobeOrderedThreatType = this.threatTypes.find((existingThreatType: ThreatType) => existingThreatType.id === threatType.id);
      tobeOrderedThreatType.numericValue = newSequenceNumber;
      newSequenceNumber++;
    });
    this.threatTypes.sort((a: ThreatType, b: ThreatType) =>
      a.numericValue < b.numericValue ? -1 : a.numericValue > b.numericValue ? 1 : 0);
    return this.threatTypes;
  }
}
